import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header";
import api from "../../utils/api";
import { getImageUrl } from "../../utils/imageUtils";
import {
  toLocalizedNumber,
  toLocalizedPrice,
  toLocalizedStatus,
} from "../../utils/localeUtils";
import {
  FaClock,
  FaCheckCircle,
  FaComments,
  FaBox,
  FaArrowRight,
  FaChartLine,
  FaClipboardList,
  FaPlus,
  FaUser,
  FaCog,
  FaMoneyBillWave,
  FaExclamationTriangle,
  FaTruck,
  FaHourglassHalf,
  FaTimesCircle,
  FaSeedling,
} from "react-icons/fa";
import "./FarmerDashboard.css";

const FarmerDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProducts: 0,
    pendingOrders: 0,
    completedOrders: 0,
    totalRevenue: 0,
    unreadMessages: 0,
  });
  const [weeklySales, setWeeklySales] = useState([]);
  const [orderActivity, setOrderActivity] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try {
        const userObj = JSON.parse(userData);
        setUser(userObj);
        if (userObj.role !== "farmer") {
          navigate("/");
          return;
        }
        fetchUserProfile();
        fetchDashboardData();
      } catch {
        setLoading(false);
      }
    } else {
      navigate("/login");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const fetchUserProfile = async () => {
    try {
      const res = await api.get("/users/profile");
      setUser(res.data);
      localStorage.setItem("user", JSON.stringify(res.data));
    } catch {
      /* ignore */
    }
  };

  const fetchDashboardData = async () => {
    try {
      const [prodRes, ordRes, convRes] = await Promise.all([
        api.get("/products/farmer/my-products"),
        api.get("/orders"),
        api.get("/messages/conversations"),
      ]);
      const products = prodRes.data || [];
      const orders = ordRes.data || [];
      const conversations = convRes.data || [];

      const revenue = orders
        .filter(
          (o) =>
            ["delivered", "received"].includes(o.orderStatus) ||
            o.paymentStatus === "paid",
        )
        .reduce((s, o) => s + (o.totalPrice || 0), 0);

      setStats({
        totalProducts: products.length,
        pendingOrders: orders.filter((o) =>
          ["pending", "confirmed"].includes(o.orderStatus),
        ).length,
        completedOrders: orders.filter((o) =>
          ["delivered", "received"].includes(o.orderStatus),
        ).length,
        totalRevenue: revenue,
        unreadMessages: conversations.reduce(
          (s, c) => s + (c.unreadCount || 0),
          0,
        ),
      });

      const now = new Date();
      const last7 = Array.from({ length: 7 }, (_, i) => {
        const d = new Date(now);
        d.setDate(d.getDate() - (6 - i));
        return d;
      });
      const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

      setWeeklySales(
        last7.map((d) => ({
          day: dayNames[d.getDay()],
          sales: orders
            .filter(
              (o) =>
                new Date(o.createdAt).toDateString() === d.toDateString() &&
                (["delivered", "received"].includes(o.orderStatus) ||
                  o.paymentStatus === "paid"),
            )
            .reduce((s, o) => s + (o.totalPrice || 0), 0),
        })),
      );

      setOrderActivity(
        last7.map((d) => ({
          day: dayNames[d.getDay()],
          orders: orders.filter(
            (o) => new Date(o.createdAt).toDateString() === d.toDateString(),
          ).length,
        })),
      );

      const sorted = [...orders].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      );
      setRecentOrders(sorted.slice(0, 5));
      setLoading(false);
    } catch {
      setLoading(false);
    }
  };

  const getTimeAgo = (date) => {
    const diff = Math.floor((Date.now() - new Date(date)) / 1000);
    if (diff < 60) return t("farmerDashboard.justNow");
    if (diff < 3600)
      return t("farmerDashboard.minutesAgo", {
        minutes: Math.floor(diff / 60),
      });
    if (diff < 86400)
      return t("farmerDashboard.hoursAgo", { hours: Math.floor(diff / 3600) });
    return t("farmerDashboard.daysAgo", { days: Math.floor(diff / 86400) });
  };

  const maxSale = Math.max(...weeklySales.map((d) => d.sales), 1);
  const maxOrd = Math.max(...orderActivity.map((d) => d.orders), 1);

  const statusMeta = {
    pending: { icon: <FaHourglassHalf />, color: "#f59e0b", bg: "#fffbeb" },
    confirmed: { icon: <FaCheckCircle />, color: "#3b82f6", bg: "#eff6ff" },
    processing: { icon: <FaClock />, color: "#8b5cf6", bg: "#f5f3ff" },
    shipped: { icon: <FaTruck />, color: "#06b6d4", bg: "#ecfeff" },
    delivered: { icon: <FaCheckCircle />, color: "#22c55e", bg: "#f0fdf4" },
    received: { icon: <FaCheckCircle />, color: "#0d9488", bg: "#f0fdfa" },
    cancelled: { icon: <FaTimesCircle />, color: "#ef4444", bg: "#fef2f2" },
  };

  const quickActions = [
    {
      to: "/farmer/products/add",
      icon: <FaPlus />,
      label: t("farmerDashboard.addProduct"),
      accent: "#4CAF50",
    },
    {
      to: "/farmer/products",
      icon: <FaSeedling />,
      label: t("farmerDashboard.myProducts"),
      accent: "#2196F3",
    },
    {
      to: "/farmer/orders",
      icon: <FaClipboardList />,
      label: t("farmerDashboard.manageOrders"),
      accent: "#FF9800",
    },
    {
      to: "/farmer/analytics",
      icon: <FaChartLine />,
      label: t("farmerDashboard.analytics"),
      accent: "#9C27B0",
    },
    {
      to: "/farmer/payments",
      icon: <FaMoneyBillWave />,
      label: t("farmerDashboard.payments"),
      accent: "#00BCD4",
    },
    {
      to: "/messages",
      icon: <FaComments />,
      label: t("farmerDashboard.messagesAction"),
      accent: "#E91E63",
      badge: stats.unreadMessages,
    },
    {
      to: "/farmer/profile",
      icon: <FaUser />,
      label: t("farmerDashboard.myProfile"),
      accent: "#607D8B",
    },
    {
      to: "/farmer/alerts",
      icon: <FaCog />,
      label: t("farmerDashboard.alerts"),
      accent: "#795548",
    },
  ];

  if (loading) {
    return (
      <div className="fd-page">
        <Header />
        <div className="fd-loading">
          <div className="fd-dots">
            <span />
            <span />
            <span />
          </div>
          <p>{t("farmerDashboard.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fd-page">
      <Header />
      <div className="fd-container">
        {/* Greeting */}
        <section className="fd-greeting">
          <div>
            <h1>
              {t("farmerDashboard.welcomeBack", {
                name: user?.name?.split(" ")[0] || "Farmer",
              })}
            </h1>
            <p>{t("farmerDashboard.subtitle")}</p>
          </div>
          <Link to="/farmer/products/add" className="fd-add-btn">
            <FaPlus /> {t("farmerDashboard.addProduct")}
          </Link>
        </section>

        {/* Validation banner */}
        {user && !user.verified && (
          <div className="fd-validation">
            <FaExclamationTriangle />
            <div>
              <strong>{t("farmerDashboard.adminValidationPending")}</strong>
              <p>{t("farmerDashboard.validationPendingMessage")}</p>
            </div>
          </div>
        )}

        {/* Stats */}
        <section className="fd-stats">
          <div className="fd-stat fd-stat--products">
            <div className="fd-stat-icon">
              <FaSeedling />
            </div>
            <div className="fd-stat-data">
              <span className="fd-stat-num">
                {toLocalizedNumber(stats.totalProducts)}
              </span>
              <span className="fd-stat-label">
                {t("farmerDashboard.totalProducts")}
              </span>
            </div>
          </div>
          <div className="fd-stat fd-stat--pending">
            <div className="fd-stat-icon">
              <FaClock />
            </div>
            <div className="fd-stat-data">
              <span className="fd-stat-num">
                {toLocalizedNumber(stats.pendingOrders)}
              </span>
              <span className="fd-stat-label">
                {t("farmerDashboard.pendingOrders")}
              </span>
            </div>
          </div>
          <div className="fd-stat fd-stat--done">
            <div className="fd-stat-icon">
              <FaCheckCircle />
            </div>
            <div className="fd-stat-data">
              <span className="fd-stat-num">
                {toLocalizedNumber(stats.completedOrders)}
              </span>
              <span className="fd-stat-label">
                {t("farmerDashboard.completedOrders")}
              </span>
            </div>
          </div>
          <div className="fd-stat fd-stat--revenue">
            <div className="fd-stat-icon">
              <FaMoneyBillWave />
            </div>
            <div className="fd-stat-data">
              <span className="fd-stat-num">
                Rs. {toLocalizedPrice(stats.totalRevenue)}
              </span>
              <span className="fd-stat-label">
                {t("farmerDashboard.totalRevenue")}
              </span>
            </div>
          </div>
          <div className="fd-stat fd-stat--messages">
            <div className="fd-stat-icon">
              <FaComments />
            </div>
            <div className="fd-stat-data">
              <span className="fd-stat-num">
                {toLocalizedNumber(stats.unreadMessages)}
              </span>
              <span className="fd-stat-label">
                {t("farmerDashboard.unreadMessages")}
              </span>
            </div>
          </div>
        </section>

        {/* Quick Actions */}
        <section className="fd-actions-section">
          <h2 className="fd-section-title">
            {t("farmerDashboard.quickActions")}
          </h2>
          <div className="fd-actions">
            {quickActions.map((a, i) => (
              <Link
                key={i}
                to={a.to}
                className="fd-action"
                style={{ "--accent": a.accent }}
              >
                <div className="fd-action-icon">{a.icon}</div>
                <span className="fd-action-label">{a.label}</span>
                {a.badge > 0 && (
                  <span className="fd-action-badge">
                    {toLocalizedNumber(a.badge)}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </section>

        {/* Charts */}
        <section className="fd-charts">
          <div className="fd-chart-card">
            <h3 className="fd-chart-title">
              <FaChartLine /> {t("farmerDashboard.weeklySales")}
            </h3>
            <div className="fd-bar-chart">
              {weeklySales.map((d, i) => (
                <div key={i} className="fd-bar-col">
                  <div className="fd-bar-track">
                    <div
                      className="fd-bar"
                      style={{ height: `${(d.sales / maxSale) * 100}%` }}
                    >
                      {d.sales > 0 && (
                        <span className="fd-bar-tip">
                          Rs.{toLocalizedPrice(d.sales)}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="fd-bar-day">{d.day}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="fd-chart-card">
            <h3 className="fd-chart-title">
              <FaClipboardList /> {t("farmerDashboard.orderActivity")}
            </h3>
            <div className="fd-bar-chart">
              {orderActivity.map((d, i) => (
                <div key={i} className="fd-bar-col">
                  <div className="fd-bar-track">
                    <div
                      className="fd-bar fd-bar--blue"
                      style={{ height: `${(d.orders / maxOrd) * 100}%` }}
                    >
                      {d.orders > 0 && (
                        <span className="fd-bar-tip">
                          {toLocalizedNumber(d.orders)}
                        </span>
                      )}
                    </div>
                  </div>
                  <span className="fd-bar-day">{d.day}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Recent Orders */}
        <section className="fd-orders-section">
          <div className="fd-section-head">
            <h2 className="fd-section-title">
              {t("farmerDashboard.recentOrders")}
            </h2>
            <Link to="/farmer/orders" className="fd-view-all">
              {t("farmerDashboard.viewAll")} <FaArrowRight />
            </Link>
          </div>
          {recentOrders.length > 0 ? (
            <div className="fd-orders">
              {recentOrders.map((order) => {
                const meta =
                  statusMeta[order.orderStatus] || statusMeta.pending;
                const firstItem = order.items?.[0];
                const imgSrc = firstItem?.product?.images?.[0]
                  ? firstItem.product.images[0].startsWith("http")
                    ? firstItem.product.images[0]
                    : getImageUrl(firstItem.product.images[0])
                  : "/images/deafultpic.png";
                return (
                  <Link
                    to={`/farmer/order/${order._id}`}
                    key={order._id}
                    className="fd-order"
                  >
                    <div
                      className="fd-order-strip"
                      style={{ background: meta.color }}
                    />
                    <div className="fd-order-thumb">
                      <img
                        src={imgSrc}
                        alt=""
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "/images/deafultpic.png";
                        }}
                      />
                    </div>
                    <div className="fd-order-body">
                      <div className="fd-order-top">
                        <span className="fd-order-name">
                          {firstItem?.name ||
                            firstItem?.product?.name ||
                            "Order"}
                          {(order.items?.length || 0) > 1 && (
                            <span className="fd-order-more">
                              {" "}
                              +{toLocalizedNumber(order.items.length - 1)}
                            </span>
                          )}
                        </span>
                        <span
                          className="fd-order-badge"
                          style={{ color: meta.color, background: meta.bg }}
                        >
                          {meta.icon} {toLocalizedStatus(order.orderStatus, t)}
                        </span>
                      </div>
                      <div className="fd-order-bottom">
                        <span className="fd-order-customer">
                          {order.consumer?.name || "Customer"}
                        </span>
                        <span className="fd-order-price">
                          Rs. {toLocalizedPrice(order.totalPrice || 0)}
                        </span>
                        <span className="fd-order-time">
                          {getTimeAgo(order.createdAt)}
                        </span>
                      </div>
                    </div>
                    <FaArrowRight className="fd-order-arrow" />
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="fd-empty">
              <FaBox className="fd-empty-icon" />
              <p>{t("farmerDashboard.noRecentOrders")}</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default FarmerDashboard;
